import { ArrowPathIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, ListBulletIcon, PlayIcon } from "@heroicons/react/20/solid";
import type { AuditFinding, AuditScore, AuditScoreType } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import { AdminPanel, AdminPanelEmpty } from "../components/AdminPanel";
import { OtsBadge, type OtsBadgeTone } from "../components/OtsBadge";
import { OtsActionButton } from "../components/OtsButton";
import { OtsConfidenceRing } from "../components/OtsConfidenceRing";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import { AuditFindings } from "./AuditFindings";
import { auditScoreLabels, formatConfidence, toConfidenceRatio } from "./AuditScoreRing";
import { type AuditWarning, auditDisplayName, isScoreCapped, partitionAuditWarnings } from "./audit-utils";

export type AuditPanelProps = {
  scores?: AuditScore[];
  findings?: AuditFinding[];
  /** The ticket's data checks (driver activity warnings); those a finding already covers are folded into it. Omit to hide Data Checks. */
  warnings?: AuditWarning[];
  isLoading?: boolean;
  /** An audit is running now. */
  isRunning?: boolean;
  error?: ReactNode;
  onRefresh?: () => void;
  onRunAudit?: () => void;
  /** Opens the full audit view (score contributions and history), as WaterGo's View button does. */
  onView?: () => void;
};

const scoreLabel = (scoreType: string) => auditScoreLabels[scoreType as AuditScoreType] ?? auditDisplayName(scoreType);

const priorityTones: Record<string, OtsBadgeTone> = { HIGH: "danger", MEDIUM: "warning" };

const Card = ({ title, detail, icon, children }: { title: string; detail?: ReactNode; icon: ReactNode; children: ReactNode }) =>
  <AdminPanel variant="dense" title={title} icon={icon} actions={detail}>{children}</AdminPanel>;

const Empty = AdminPanelEmpty;

/** One confidence score: the ring, its percentage, and how many rules fed it. */
export const AuditScoreCard = ({ score }: { score: AuditScore }) => {
  const noEvidence = score.status === "NO_EVIDENCE";
  const label = scoreLabel(score.scoreType);
  const percent = noEvidence ? "No evidence" : score.confidence != null ? formatConfidence(score.confidence) : "Unknown";

  return (
    <div data-score={score.scoreType} className="flex min-w-0 items-center gap-2.5 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] px-3 py-2">
      <OtsConfidenceRing size={30} value={noEvidence ? undefined : toConfidenceRatio(score.confidence)} label={`${label}: ${percent}`}/>
      <div className="min-w-0">
        <p className="truncate text-[13px] font-bold leading-4">{label}</p>
        <p className="text-xs font-bold leading-4 tabular-nums">{percent}</p>
        <p className="truncate text-[11px] leading-4 text-[var(--ots-text-muted)]">
          {score.impacts?.length ?? 0} impacts · {score.contributions?.length ?? 0} contributions
        </p>
        {isScoreCapped(score) && (
          <p className="text-[11px] font-bold leading-4 text-[var(--ots-on-warning-container)]">Capped from {formatConfidence(score.uncappedConfidence)}</p>
        )}
      </div>
    </div>
  );
};

/**
 * A ticket's audit, with what WaterGo's Ticket Audit dialog shows: a card per confidence score, the active
 * findings, and the driver-activity data checks the findings do not already cover.
 */
export const AuditPanel = ({ scores, findings, warnings, isLoading = false, isRunning = false, error, onRefresh, onRunAudit, onView }: AuditPanelProps) => {
  const busy = isLoading || isRunning;
  const actions = (onRefresh || onRunAudit || onView) && (
    <div className="flex flex-wrap gap-2">
      {onView && <OtsActionButton size="compact" icon={<ArrowTopRightOnSquareIcon/>} onClick={onView}>View</OtsActionButton>}
      {onRefresh && <OtsActionButton size="compact" disabled={busy} icon={<ArrowPathIcon className={isLoading ? "animate-spin" : undefined}/>} onClick={onRefresh}>Refresh</OtsActionButton>}
      {onRunAudit && <OtsActionButton size="compact" variant="primary" disabled={busy} icon={<PlayIcon/>} onClick={onRunAudit}>{isRunning ? "Running audit…" : "Run Audit"}</OtsActionButton>}
    </div>
  );

  if (error != null) {
    return <div className="space-y-2">
      <OtsInlineAlert tone="danger" size="compact" title="The audit could not be loaded">{error}</OtsInlineAlert>
      {actions}
    </div>;
  }
  if (isRunning) return <p role="status" className="p-3 text-center text-xs text-[var(--ots-text-muted)]">Running audit…</p>;
  if (isLoading && !findings && !scores) return <p role="status" className="p-3 text-center text-xs text-[var(--ots-text-muted)]">Loading audit results…</p>;

  const active = findings ?? [];
  const checks = warnings && partitionAuditWarnings(warnings, active);

  return (
    <div className="@container space-y-3">
      {scores?.length
        ? <div aria-label="Confidence scores" role="group" className="grid gap-2 @md:grid-cols-2 @2xl:grid-cols-3">
          {scores.map((score) => <AuditScoreCard key={score.scoreType} score={score}/>)}
        </div>
        : <Empty>No confidence scores returned.</Empty>}

      <Card title="Findings" detail={`${active.length} active`} icon={<ExclamationTriangleIcon/>}>
        <div className="max-h-96 overflow-y-auto p-2">
          {active.length ? <AuditFindings findings={active}/> : <Empty>No active findings.</Empty>}
        </div>
      </Card>

      {checks && (
        <Card
          title="Data Checks"
          detail={`${checks.remaining.length} separate${checks.covered.length ? ` · ${checks.covered.length} consolidated` : ""}`}
          icon={<ListBulletIcon/>}>
          {checks.covered.length > 0 && (
            <p className="border-b border-[var(--ots-outline-variant)] bg-[var(--ots-primary-container)] px-3 py-1.5 text-[11px] leading-4 text-[var(--ots-primary)]">
              {checks.covered.length} activity warning{checks.covered.length === 1 ? " is" : "s are"} already represented by the active audit findings above.
            </p>
          )}
          {checks.remaining.length
            ? <ul aria-label="Data checks" className="max-h-60 divide-y divide-[var(--ots-outline-variant)] overflow-y-auto">
              {checks.remaining.map((warning) => (
                <li key={`${warning.type}-${warning.message}`} className="px-3 py-2">
                  <div className="flex items-start gap-2">
                    <span className="min-w-0 flex-1 text-xs font-bold leading-4">{warning.message || "Driver activity warning"}</span>
                    <OtsBadge tone={priorityTones[warning.priority?.toUpperCase() ?? ""] ?? "info"} size="compact">{auditDisplayName(warning.priority || "LOW").toLowerCase()}</OtsBadge>
                  </div>
                  <p className="mt-0.5 text-[11px] text-[var(--ots-text-muted)]">{auditDisplayName(warning.type).toLowerCase()}</p>
                </li>
              ))}
            </ul>
            : <Empty>{warnings?.length ? "No additional driver activity warnings outside the audit findings." : "No driver activity warnings."}</Empty>}
        </Card>
      )}
      {actions}
    </div>
  );
};
